/* @flow */

import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import classNames from 'classnames';

import {ActionMenu, type SelectMenuOption} from '../../action-menu';
import {SavedSettingToast, type SavedSettingToastState} from '../../saved-setting-toast';
import {StatusBadge, type Level} from '../../status-badge';
import {Toolbar, type ToolbarOption, type ActionMenuOptions} from '../../toolbar/toolbar';
import {Body} from '../../typography';

import {RowIcon} from '../row-icon';
import type {RowType} from '../types';

import {
    TertiaryAction,
    type TertiaryActionProps,
    type TertiaryActionPropsButton,
} from './tertiary-action';

import './base-row.module.css';

type BackgroundColorType = 'default' | 'green';
export type BaseRowStatusBadge = {|
    text: string,
    level: Level,
    icon: React.ComponentType<*>,
    detail?: string,
    onClick?: (e: SyntheticEvent<HTMLElement>) => void,
    getRef?: (ref: ?HTMLElement) => void,
|};

export type ToolbarProps = {|
    options: ToolbarOption[],
    actionMenuOptions?: ActionMenuOptions,
    forceShowToolbar?: boolean,
    forceHideToolbar?: boolean,
|};

type Props = {|
    type: RowType,
    url?: string,
    onClick?: (PointerEvent) => void,
    handleMouseOver?: (e: MouseEvent) => mixed,
    handleMouseLeave?: (e: MouseEvent) => mixed,
    actionMenuOptions?: SelectMenuOption[],
    getActionMenuRef?: (?HTMLElement) => void,
    getRowRef?: {current: ?HTMLElement},
    children: React.Node,
    title?: string,
    titleColor?: 'dark' | 'light',
    titleIsStrikethrough?: boolean,
    overflowHidden?: boolean,

    // Optional save toast state; when provided, shows spinner/saved next to title
    saveToastState?: SavedSettingToastState,
    setSaveToastState?: (state: SavedSettingToastState) => void,

    tertiaryProps?: TertiaryActionProps,
    quaternaryProps?: TertiaryActionPropsButton,

    // Should only be used for thing like hoverviews that require external
    // API calls. Might also change for when we have other decals on the icons
    // such as primary, parent company, etc.
    customIcon?: React.Node,

    statusBadges?: BaseRowStatusBadge[],
    toolbarProps?: ToolbarProps,

    backgroundColor?: BackgroundColorType,
    // If true, the row will have an onHover effect without needing an props.onClick or props.url
    isHoverable?: boolean,
    tooltipText?: ?string,
|};

export function BaseRow(props: Props) {
    const {titleColor = 'dark', backgroundColor = 'default'} = props;

    const [shouldShowToolbar, setShouldShowToolbar] = React.useState<boolean>(false);

    const shouldRenderToolbar =
        props.toolbarProps &&
        (shouldShowToolbar || props.toolbarProps.forceShowToolbar) &&
        !props.toolbarProps.forceHideToolbar &&
        props.toolbarProps.options.length > 0;

    const title = (
        <Body
            color={titleColor}
            isBold={true}
            wrapAnywhere={true}
            strikethrough={props.titleIsStrikethrough}
        >
            {props.title}
        </Body>
    );

    const childrenClassName = classNames('flex flex-dir-col flex-grow gap-2 justify-center', {
        'overflow-hidden': props.overflowHidden,
    });

    return (
        <BaseRowWrapper
            url={props.url}
            getRef={props.getRowRef}
            onClick={props.onClick}
            handleMouseOver={props.handleMouseOver}
            handleMouseLeave={props.handleMouseLeave}
            setShouldShowToolbar={setShouldShowToolbar}
            backgroundColor={backgroundColor}
            isHoverable={props.isHoverable}
            tooltipText={props.tooltipText}
        >
            <div styleName='icon'>{props.customIcon || <RowIcon type={props.type} />}</div>
            <div className={childrenClassName}>
                {props.title &&
                    (props.saveToastState ? (
                        <SavedSettingToast
                            state={props.saveToastState}
                            onStateChange={props.setSaveToastState}
                            autoResetDelay={1500}
                            largerSpinner={true}
                        >
                            {title}
                        </SavedSettingToast>
                    ) : (
                        title
                    ))}
                {props.statusBadges &&
                    props.statusBadges.map((statusBadge) => {
                        return (
                            <div key={statusBadge.text} className='py-4'>
                                <StatusBadge
                                    level={statusBadge.level}
                                    text={statusBadge.text}
                                    customIcon={statusBadge.icon}
                                    detail={statusBadge.detail}
                                    size='small'
                                    onClick={statusBadge.onClick}
                                    getRef={statusBadge.getRef}
                                />
                            </div>
                        );
                    })}
                {props.children}
            </div>
            {(props.tertiaryProps || props.quaternaryProps) && (
                <div styleName='actions'>
                    {/* $FlowFixMe I don't know why this is complaining */}
                    {props.tertiaryProps && <TertiaryAction {...props.tertiaryProps} />}
                    {props.quaternaryProps && <TertiaryAction {...props.quaternaryProps} />}
                </div>
            )}
            {props.actionMenuOptions && props.actionMenuOptions.length > 0 && (
                <ActionMenu
                    size='small'
                    hasHoverBackground={false}
                    fitContent={true}
                    options={props.actionMenuOptions}
                    getRef={props.getActionMenuRef}
                />
            )}
            {shouldRenderToolbar && props.toolbarProps && (
                <div styleName='toolbar-container'>
                    <Toolbar
                        options={props.toolbarProps.options}
                        actionMenuOptions={props.toolbarProps.actionMenuOptions}
                    />
                </div>
            )}
        </BaseRowWrapper>
    );
}

type BaseRowWrapperProps = {|
    url?: string,
    getRef?: {current: ?HTMLElement},
    onClick?: (PointerEvent) => void,
    handleMouseOver?: (e: MouseEvent) => mixed,
    handleMouseLeave?: (e: MouseEvent) => mixed,
    children: React.Node,
    setShouldShowToolbar: (shouldShowToolbar: boolean) => void,
    backgroundColor?: BackgroundColorType,
    isHoverable?: boolean,
    tooltipText?: ?string,
|};

function BaseRowWrapper(props: BaseRowWrapperProps) {
    const [isDragging, setIsDragging] = React.useState(false);

    const handleClick = (e: PointerEvent) => {
        if (isDragging) {
            e.preventDefault();
            setIsDragging(false);
        } else if (props.onClick) {
            props.onClick(e);
        }
    };

    const handleDragStart = (e: DragEvent) => {
        // Prevent the link from being draggable so text selection works
        e.preventDefault();
    };

    const handleMouseDown = () => {
        const selection = window.getSelection();
        // Clear the selection to allow selecting then clicking the row
        selection.removeAllRanges();
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        const selection = window.getSelection();
        setIsDragging(selection && selection.toString().length > 0);
    };

    const linkStyleNames = classNames('row--clickable hoverable', {
        'bg-green bg-green-hover': props.backgroundColor === 'green',
    });

    if (props.url) {
        return (
            <RoutedLink
                to={props.url}
                styleName={linkStyleNames}
                onClick={handleClick}
                onDragStart={handleDragStart}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseOver={(e) => {
                    props.handleMouseOver && props.handleMouseOver(e);
                    props.setShouldShowToolbar(true);
                }}
                onMouseLeave={(e) => {
                    props.handleMouseLeave && props.handleMouseLeave(e);
                    props.setShouldShowToolbar(false);
                }}
                // $FlowIgnore this is OK
                ref={props.getRef}
                data-tip={props.tooltipText || undefined}
            >
                {props.children}
            </RoutedLink>
        );
    }

    const rowStyleNames = classNames({
        row: !props.onClick,
        'row--clickable hoverable': props.onClick,
        'bg-green': props.backgroundColor === 'green',
        'bg-green-hover': props.backgroundColor === 'green' && (props.onClick || props.isHoverable),
        hoverable: props.isHoverable,
    });

    return (
        <div
            styleName={rowStyleNames}
            ref={props.getRef}
            onClick={handleClick}
            onMouseOver={() => props.setShouldShowToolbar(true)}
            onMouseLeave={() => props.setShouldShowToolbar(false)}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            data-tip={props.tooltipText}
        >
            {props.children}
        </div>
    );
}
